import { useState, useEffect, useRef } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft,
  Check,
  X,
  Info,
  Scale,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Shield,
  ShieldCheck,
  Clock,
  Heart,
  Building2,
  Ambulance,
  Stethoscope,
  Pill,
  Percent,
  Users,
  AlertCircle,
  TrendingUp,
  Award,
  Timer,
  CheckCircle2,
  Phone,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Home,
  RefreshCw,
  Activity
} from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { trackComparison, trackPDFGeneration } from "@/utils/analytics";
import ClickableFeature from "@/components/ClickableFeature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// IRDAI Insurance Statistics Handbook 2024-25 (Published February 2025)
// Data Year: FY 2024-25 (April 2024 - March 2025)
const CLAIM_EXPERIENCE_DATA = {
  "hdfc-ergo": {
    claim_settlement_ratio: "98.33%",
    incurred_claim_ratio: "87.04%",
    icr_previous_year: "84.85%",
    icr_trend: "Increased",
    complaints: "7,326",
    complaints_per_10k: "2.3",  // 7326 complaints / 3174917 claims paid * 10000
    solvency_ratio: "2.00",
    gross_premium_cr: "₹5,720 Cr",
    network_hospitals: "13,500+",
    cashless_hospitals: "10,500+",
    network_vs_cashless_note: "78% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "care-health": {
    claim_settlement_ratio: "96.85%",
    incurred_claim_ratio: "65.10%",
    icr_previous_year: "64.53%",
    icr_trend: "Stable",
    complaints: "10,281",
    complaints_per_10k: "7.2",  // 10281 complaints / 1436282 claims paid * 10000
    solvency_ratio: "1.68",
    gross_premium_cr: "₹8,018 Cr",
    network_hospitals: "25,000+",
    cashless_hospitals: "21,500+",
    network_vs_cashless_note: "86% cashless",
    irdai_health_category: "Standalone Health Insurer",
    data_year: "FY 2024-25"
  },
  "icici-lombard": {
    claim_settlement_ratio: "90.72%",
    incurred_claim_ratio: "85.53%",
    icr_previous_year: "82.24%",
    icr_trend: "Increased",
    complaints: "7,782",
    complaints_per_10k: "2.8",  // 7782 complaints / 2808584 claims paid * 10000
    solvency_ratio: "2.69",
    gross_premium_cr: "₹6,950 Cr",
    network_hospitals: "11,000+",
    cashless_hospitals: "9,500+",
    network_vs_cashless_note: "86% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "aditya-birla": {
    claim_settlement_ratio: "95.94%",
    incurred_claim_ratio: "75.97%",
    icr_previous_year: "71.50%",
    icr_trend: "Increased",
    complaints: "5,329",
    complaints_per_10k: "2.3",  // 5329 complaints / 2284121 claims paid * 10000
    solvency_ratio: "1.84",
    gross_premium_cr: "₹4,397 Cr",
    network_hospitals: "10,800+",
    cashless_hospitals: "10,500+",
    network_vs_cashless_note: "97% cashless",
    irdai_health_category: "Standalone Health Insurer",
    data_year: "FY 2024-25"
  },
  "manipal-cigna": {
    claim_settlement_ratio: "93.71%",
    incurred_claim_ratio: "73.31%",
    icr_previous_year: "64%",
    icr_trend: "Increased",
    complaints: "2,041",
    complaints_per_10k: "4.5",  // 2041 complaints / 458566 claims paid * 10000
    solvency_ratio: "1.76",
    gross_premium_cr: "₹1,765 Cr",
    network_hospitals: "8,200+",
    cashless_hospitals: "7,800+",
    network_vs_cashless_note: "95% cashless",
    irdai_health_category: "Standalone Health Insurer",
    data_year: "FY 2024-25"
  },
  "niva-bupa": {
    claim_settlement_ratio: "92.55%",
    incurred_claim_ratio: "61.63%",
    icr_previous_year: "59.02%",
    icr_trend: "Increased",
    complaints: "7,970",
    complaints_per_10k: "8.5",  // 7970 complaints / 942543 claims paid * 10000
    solvency_ratio: "3.03",
    gross_premium_cr: "₹6,616 Cr",
    network_hospitals: "10,500+",
    cashless_hospitals: "10,000+",
    network_vs_cashless_note: "95% cashless",
    irdai_health_category: "Standalone Health Insurer",
    data_year: "FY 2024-25"
  },
  "tata-aig": {
    claim_settlement_ratio: "96.63%",
    incurred_claim_ratio: "80.65%",
    icr_previous_year: "72.5%",
    icr_trend: "Increased",
    complaints: "3,577",
    complaints_per_10k: "1.8",  // 3577 complaints / 1953412 claims paid * 10000
    solvency_ratio: "1.81",
    gross_premium_cr: "₹2,921 Cr",
    network_hospitals: "10,200+",
    cashless_hospitals: "9,800+",
    network_vs_cashless_note: "96% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "star-health": {
    claim_settlement_ratio: "86.42%",
    incurred_claim_ratio: "70.53%",
    icr_previous_year: "68.2%",
    icr_trend: "Increased",
    complaints: "20,527",
    complaints_per_10k: "10.2",  // 20527 complaints / 2022177 claims paid * 10000
    solvency_ratio: "2.21",
    gross_premium_cr: "₹16,517 Cr",
    network_hospitals: "14,500+",
    cashless_hospitals: "14,000+",
    network_vs_cashless_note: "97% cashless",
    irdai_health_category: "Standalone Health Insurer",
    data_year: "FY 2024-25"
  },
  "bajaj-allianz": {
    claim_settlement_ratio: "98.08%",
    incurred_claim_ratio: "90.01%",
    icr_previous_year: "84.96%",
    icr_trend: "Increased",
    complaints: "3,807",
    complaints_per_10k: "0.6",  // 3807 complaints / 6392549 claims paid * 10000
    solvency_ratio: "3.25",
    gross_premium_cr: "₹7,637 Cr",
    network_hospitals: "18,800+",
    cashless_hospitals: "18,500+",
    network_vs_cashless_note: "98% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "digit": {
    claim_settlement_ratio: "96.83%",
    incurred_claim_ratio: "90.06%",
    icr_previous_year: "78%",
    icr_trend: "Increased",
    complaints: "2,362",
    complaints_per_10k: "2.6",  // 2362 complaints / 915678 claims paid * 10000
    solvency_ratio: "2.24",
    gross_premium_cr: "₹1,178 Cr",
    network_hospitals: "11,500+",
    cashless_hospitals: "11,200+",
    network_vs_cashless_note: "97% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "new-india": {
    claim_settlement_ratio: "98.44%",
    incurred_claim_ratio: "101.28%",
    icr_previous_year: "103%",
    icr_trend: "Improved",
    complaints: "7,768",
    complaints_per_10k: "0.6",  // 7768 complaints / 12821714 claims paid * 10000
    solvency_ratio: "1.91",
    gross_premium_cr: "₹19,188 Cr",
    network_hospitals: "8,800+",
    cashless_hospitals: "8,500+",
    network_vs_cashless_note: "97% cashless",
    irdai_health_category: "PSU General Insurer",
    data_year: "FY 2024-25"
  },
  "sbi-general": {
    claim_settlement_ratio: "98.43%",
    incurred_claim_ratio: "94.20%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "4,503",
    complaints_per_10k: "3.0",  // 4503 complaints / 1477063 claims paid * 10000
    solvency_ratio: "2.03",
    gross_premium_cr: "₹3,433 Cr",
    network_hospitals: "9,000+",
    cashless_hospitals: "8,500+",
    network_vs_cashless_note: "94% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "reliance-general": {
    claim_settlement_ratio: "97.72%",
    incurred_claim_ratio: "97.10%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "3,396",
    complaints_per_10k: "0.8",  // 3396 complaints / 4129912 claims paid * 10000
    solvency_ratio: "1.59",
    gross_premium_cr: "₹1,771 Cr",
    network_hospitals: "8,000+",
    cashless_hospitals: "7,500+",
    network_vs_cashless_note: "94% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "royal-sundaram": {
    claim_settlement_ratio: "94.23%",
    incurred_claim_ratio: "97.20%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "1,052",
    complaints_per_10k: "3.1",  // 1052 complaints / 335657 claims paid * 10000
    solvency_ratio: "2.20",
    gross_premium_cr: "₹703 Cr",
    network_hospitals: "7,000+",
    cashless_hospitals: "6,500+",
    network_vs_cashless_note: "93% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "future-generali": {
    claim_settlement_ratio: "96.58%",
    incurred_claim_ratio: "99.45%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "1,211",
    complaints_per_10k: "1.8",  // 1211 complaints / 681513 claims paid * 10000
    solvency_ratio: "1.96",
    gross_premium_cr: "₹1,730 Cr",
    network_hospitals: "8,500+",
    cashless_hospitals: "8,000+",
    network_vs_cashless_note: "94% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "cholamandalam": {
    claim_settlement_ratio: "92.82%",
    incurred_claim_ratio: "86.63%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "3,467",
    complaints_per_10k: "6.4",  // 3467 complaints / 541403 claims paid * 10000
    solvency_ratio: "2.18",
    gross_premium_cr: "₹891 Cr",
    network_hospitals: "7,200+",
    cashless_hospitals: "6,800+",
    network_vs_cashless_note: "94% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "iffco-tokio": {
    claim_settlement_ratio: "96.41%",
    incurred_claim_ratio: "85.31%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "3,697",
    complaints_per_10k: "4.8",  // 3697 complaints / 768176 claims paid * 10000
    solvency_ratio: "1.85",
    gross_premium_cr: "₹788 Cr",
    network_hospitals: "7,500+",
    cashless_hospitals: "7,000+",
    network_vs_cashless_note: "93% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  },
  "acko": {
    claim_settlement_ratio: "97.89%",
    incurred_claim_ratio: "58.48%",
    icr_previous_year: "N/A",
    icr_trend: "N/A",
    complaints: "1,441",
    complaints_per_10k: "3.6",  // 1441 complaints / 397545 claims paid * 10000
    solvency_ratio: "2.30",
    gross_premium_cr: "₹887 Cr",
    network_hospitals: "5,500+",
    cashless_hospitals: "5,200+",
    network_vs_cashless_note: "95% cashless",
    irdai_health_category: "Private General Insurer",
    data_year: "FY 2024-25"
  }
};

// Life Insurance Claim Experience data (IRDAI FY 2023-24 Individual Death Claims)
const LIFE_CLAIM_EXPERIENCE_DATA = {
  "hdfc-life": {
    claim_settlement_ratio: "99.54%",
    csr_previous_year: "99.41%",
    csr_trend: "Increased",
    amount_settlement_ratio: "95.70%",
    settlement_within_30_days: "97%",
    irdai_category: "Private Life Insurer",
    csr_fy_2020_21: "98.01%",
    csr_fy_2021_22: "98.66%",
    csr_fy_2022_23: "99.41%"
  },
  "tata-aia-life": {
    claim_settlement_ratio: "99.13%",
    csr_previous_year: "99.03%",
    csr_trend: "Increased",
    amount_settlement_ratio: "94.5%",
    settlement_within_30_days: "99.18%",
    irdai_category: "Private Life Insurer",
    csr_fy_2020_21: "98.02%",
    csr_fy_2021_22: "98.54%",
    csr_fy_2022_23: "99.03%"
  }
};

// Comparison categories with icons
const COMPARISON_CATEGORIES = [
  {
    id: "claim_experience",
    name: "Claim Experience (IRDAI FY 2024-25)",
    icon: Award,
    color: "from-[#EDF1F8] to-cyan-500",
    description: "Official IRDAI Insurance Statistics Handbook 2024-25 data. These metrics reflect company-level performance.",
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)", tooltip: "% of claims PAID out of total decided. Higher is better. 95%+ is excellent." },
      { key: "incurred_claim_ratio", label: "Incurred Claim Ratio (ICR) FY 24-25", tooltip: "Claims paid vs premiums earned. Lower indicates healthier finances. Above 100% = paying more than earning." },
      { key: "icr_previous_year", label: "ICR Previous Year (FY 23-24)", tooltip: "Last year's ICR for trend comparison." },
      { key: "icr_trend", label: "ICR Trend", tooltip: "Whether ICR increased, decreased, or remained stable compared to previous year." },
      { key: "solvency_ratio", label: "Solvency Ratio (Mar 2025)", tooltip: "Financial health indicator. IRDAI mandates minimum 1.5. Higher = more secure. Above 2.0 is excellent." },
      { key: "complaints_per_10k", label: "Complaints per 10,000 Claims", tooltip: "Normalized complaint ratio for fair comparison across insurers. Lower is better." },
      { key: "gross_premium_cr", label: "Gross Premium (FY 24-25)", tooltip: "Total premium collected. Indicates market size and customer trust." },
      { key: "network_hospitals", label: "Network Hospitals", tooltip: "Total hospitals in the insurer's network where you can avail treatment." },
      { key: "cashless_hospitals", label: "Cashless Hospitals", tooltip: "Hospitals where insurer settles directly - you pay NOTHING upfront. Always prefer cashless." },
      { key: "irdai_health_category", label: "IRDAI Category", tooltip: "Standalone Health Insurer (SAHI) focuses only on health. General Insurer offers multiple products." },
    ]
  },
  {
    id: "basic",
    name: "Basic Policy Details",
    icon: Shield,
    color: "from-[#EDF1F8] to-[#DCE4F2]",
    fields: [
      { key: "sum_insured_display", label: "Sum Insured Options" },
      { key: "room_rent", label: "Room Rent" },
      { key: "icu_limit", label: "ICU Limit" },
      { key: "reasonable_customary_charges", label: "⚠️ Reasonable & Customary Charges (R&C)" },
      { key: "policy_tenure", label: "Policy Tenure" },
      { key: "family_floater", label: "Family Floater" },
    ]
  },
  {
    id: "hospitalization",
    name: "Hospitalization Coverage",
    icon: Building2,
    color: "from-emerald-500 to-green-500",
    fields: [
      { key: "pre_hospitalization", label: "Pre-Hospitalization" },
      { key: "post_hospitalization", label: "Post-Hospitalization" },
      { key: "day_care", label: "Day Care Procedures" },
      { key: "domiciliary", label: "Domiciliary Treatment" },
      { key: "organ_donor", label: "Organ Donor Coverage" },
    ]
  },
  {
    id: "ambulance",
    name: "Ambulance & Emergency",
    icon: Ambulance,
    color: "from-red-500 to-rose-500",
    fields: [
      { key: "road_ambulance", label: "Road Ambulance" },
      { key: "air_ambulance", label: "Air Ambulance" },
    ]
  },
  {
    id: "restore",
    name: "Restore & Bonus Benefits",
    icon: Percent,
    color: "from-[#D4A017] to-yellow-500",
    fields: [
      { key: "restore_benefit", label: "Restore/Recharge Benefit" },
      { key: "ncb", label: "No Claim Bonus (NCB)" },
      { key: "ncb_reduction", label: "NCB Reduction on Claim" },
    ]
  },
  {
    id: "ayush",
    name: "AYUSH & Alternative",
    icon: Heart,
    color: "from-green-500 to-emerald-500",
    fields: [
      { key: "ayush_coverage", label: "AYUSH Coverage" },
      { key: "ayush_pre_post", label: "AYUSH Pre/Post Hospitalization" },
    ]
  },
  {
    id: "waiting",
    name: "Waiting Periods",
    icon: Clock,
    color: "from-[#D4A017] to-[#D9A82C]",
    fields: [
      { key: "initial_waiting", label: "Initial Waiting Period" },
      { key: "specific_disease_waiting", label: "Specific Disease Waiting" },
      { key: "pre_existing_waiting", label: "Pre-Existing Disease (PED)" },
      { key: "ped_modification", label: "PED Modification Option" },
    ]
  },
  {
    id: "additional",
    name: "Additional Benefits",
    icon: Stethoscope,
    color: "from-cyan-500 to-[#DCE4F2]",
    fields: [
      { key: "health_checkup", label: "Annual Health Checkup" },
      { key: "consumables", label: "Consumables Coverage" },
      { key: "robotics", label: "Robotic Surgery" },
      { key: "second_opinion", label: "Second E-Opinion" },
    ]
  },
  {
    id: "optional",
    name: "Optional Coverages",
    icon: Plus,
    color: "from-pink-500 to-rose-500",
    fields: [
      { key: "maternity", label: "Maternity Coverage" },
      { key: "opd", label: "OPD Coverage" },
      { key: "hospital_cash", label: "Hospital Daily Cash" },
      { key: "critical_illness", label: "Critical Illness" },
    ]
  },
  {
    id: "copay",
    name: "Co-payments & Limits",
    icon: AlertCircle,
    color: "from-yellow-500 to-[#D9A82C]",
    fields: [
      { key: "copay_age", label: "Co-payment (Age Based)" },
      { key: "copay_zone", label: "Zonal Co-payment" },
      { key: "copay_room", label: "Room Upgrade Co-payment" },
      { key: "sub_limits", label: "Sub-Limits" },
    ]
  },
];

// Term/Life Insurance Comparison Categories (Beshak-style structure)
const TERM_COMPARISON_CATEGORIES = [
  {
    id: "plan_info",
    name: "Plan Information",
    icon: Info,
    color: "from-[#EDF1F8] to-cyan-500",
    fields: [
      { key: "plan_type", label: "Plan Type" },
      { key: "uin", label: "UIN Number" },
      { key: "sum_assured_range", label: "Sum Assured Range" },
      { key: "entry_age", label: "Entry Age" },
      { key: "maturity_age", label: "Max Maturity Age" },
      { key: "policy_term", label: "Policy Term" },
    ]
  },
  {
    id: "death_benefit",
    name: "Death Benefit",
    icon: Shield,
    color: "from-red-500 to-rose-500",
    fields: [
      { key: "level_cover", label: "Level Cover" },
      { key: "increasing_cover", label: "Increasing Cover" },
      { key: "decreasing_cover", label: "Decreasing Cover" },
      { key: "whole_life_option", label: "Whole Life Option" },
      { key: "payout_lump_sum", label: "Lump Sum Payout" },
      { key: "payout_staggered", label: "Staggered/Income Payout" },
      { key: "payout_lump_plus_income", label: "Lump Sum + Income Payout" },
      { key: "accidental_death_benefit", label: "Accidental Death Benefit" },
    ]
  },
  {
    id: "flexibility",
    name: "Flexibility",
    icon: RefreshCw,
    color: "from-purple-500 to-pink-500",
    fields: [
      { key: "regular_pay", label: "Regular Pay" },
      { key: "limited_pay", label: "Limited Pay" },
      { key: "single_pay", label: "Single Pay" },
      { key: "rolling_limited_pay", label: "Rolling Limited Pay" },
      { key: "premium_break", label: "Premium Break/Holiday" },
      { key: "early_exit_benefit", label: "Early Exit Benefit" },
      { key: "return_of_premium", label: "Return of Premium (ROP)" },
      { key: "cover_continuation", label: "Cover Continuation Option" },
    ]
  },
  {
    id: "discounts",
    name: "Discounts",
    icon: Percent,
    color: "from-green-500 to-emerald-500",
    fields: [
      { key: "women_discount", label: "Women's Discount" },
      { key: "high_sa_discount", label: "High Sum Assured Discount" },
      { key: "online_discount", label: "Online Purchase Discount" },
      { key: "annual_mode_discount", label: "Annual Mode Discount" },
      { key: "employee_discount", label: "Employee/Staff Discount" },
      { key: "healthy_lifestyle_discount", label: "Healthy Lifestyle Discount" },
    ]
  },
  {
    id: "claims",
    name: "Claims",
    icon: Award,
    color: "from-[#D4A017] to-yellow-500",
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)" },
      { key: "csr_previous_year", label: "CSR Previous Year" },
      { key: "claims_settled_in_30_days", label: "Claims Settled in 30 Days" },
      { key: "claim_rejection_rate", label: "Claim Rejection Rate" },
      { key: "grievance_ratio", label: "Grievance/Complaint Ratio" },
      { key: "irdai_category", label: "IRDAI Category" },
    ]
  },
  {
    id: "riders",
    name: "Riders & Add-ons",
    icon: ShieldCheck,
    color: "from-cyan-500 to-[#DCE4F2]",
    fields: [
      { key: "critical_illness_rider", label: "Critical Illness Rider" },
      { key: "ci_conditions_count", label: "CI Conditions Covered" },
      { key: "accidental_death_rider", label: "Accidental Death Rider" },
      { key: "accidental_disability_rider", label: "Accidental Disability Rider" },
      { key: "waiver_of_premium", label: "Waiver of Premium Rider" },
      { key: "terminal_illness_benefit", label: "Terminal Illness Benefit" },
    ]
  },
  {
    id: "additional_benefits",
    name: "Additional Benefits",
    icon: Heart,
    color: "from-pink-500 to-rose-500",
    fields: [
      { key: "spouse_cover", label: "Spouse Cover Option" },
      { key: "joint_life_cover", label: "Joint Life Cover" },
      { key: "child_education_benefit", label: "Child Education Benefit" },
      { key: "life_stage_benefit", label: "Life Stage Benefit" },
      { key: "wellness_benefits", label: "Wellness Benefits" },
      { key: "instant_claim_payout", label: "Instant Claim Payout" },
    ]
  },
];

// Personal Accident Insurance Comparison Categories
const PA_COMPARISON_CATEGORIES = [
  {
    id: "plan_info",
    name: "Plan Information",
    icon: Info,
    color: "from-[#EDF1F8] to-cyan-500",
    fields: [
      { key: "sum_insured_range", label: "Cover Amount Range" },
      { key: "entry_age", label: "Entry Age" },
      { key: "policy_term", label: "Policy Term" },
      { key: "family_cover", label: "Family Coverage" },
      { key: "medical_tests", label: "Medical Tests Needed" },
      { key: "worldwide_cover", label: "Worldwide Cover" },
    ]
  },
  {
    id: "core_covers",
    name: "Core Covers (The 4 Pillars)",
    icon: Shield,
    color: "from-orange-500 to-amber-500",
    fields: [
      { key: "accidental_death", label: "Accidental Death" },
      { key: "ptd", label: "Permanent Total Disablement (PTD)" },
      { key: "ppd", label: "Permanent Partial Disablement (PPD)" },
      { key: "ttd", label: "Weekly Income (TTD)" },
      { key: "ttd_duration", label: "TTD Max Duration" },
    ]
  },
  {
    id: "extra_benefits",
    name: "Extra Benefits",
    icon: Plus,
    color: "from-pink-500 to-rose-500",
    fields: [
      { key: "child_education", label: "Child Education Benefit" },
      { key: "hospital_cash", label: "Hospital Cash" },
      { key: "medical_expenses", label: "Accident Medical Expenses" },
      { key: "air_ambulance", label: "Air Ambulance / Evacuation" },
      { key: "broken_bones", label: "Broken Bones Cover" },
      { key: "burns", label: "Burns Cover" },
    ]
  },
  {
    id: "premium_policy",
    name: "Premium & Policy Care",
    icon: Percent,
    color: "from-green-500 to-emerald-500",
    fields: [
      { key: "premium_basis", label: "Premium Based On" },
      { key: "payment_modes", label: "Payment Modes" },
      { key: "free_look", label: "Free Look Period" },
      { key: "grace_period", label: "Grace Period" },
      { key: "renewal", label: "Renewability" },
    ]
  },
  {
    id: "claims",
    name: "Claims",
    icon: Award,
    color: "from-[#D4A017] to-yellow-500",
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)" },
      { key: "settlement_time", label: "Settlement Timeline" },
      { key: "waiting_period", label: "Waiting Period" },
    ]
  },
];

// Helper function to extract comparison data from PA plan
const extractPAComparisonData = (plan) => {
  if (!plan) return {};
  
  const getCover = (id) => plan.core_covers?.find(c => c.id === id);
  const getBenefit = (kw) => plan.additional_benefits?.find(b => b.name?.toLowerCase().includes(kw));
  const hasOptional = (kw) => plan.optional_covers?.some(o => o.name?.toLowerCase().includes(kw));
  const opts = plan.sum_insured_options || [];
  
  return {
    sum_insured_range: opts.length ? `${opts[0]} - ${opts[opts.length - 1]}` : "As per schedule",
    entry_age: plan.policy_options?.entry_age || "N/A",
    policy_term: plan.policy_options?.policy_term || "N/A",
    family_cover: plan.family_coverage ? "✓ Self, Spouse, Children & Parents" : "Individual only",
    medical_tests: plan.policy_options?.medical_tests ? "✓ Generally not required" : "As per underwriting",
    worldwide_cover: plan.worldwide_cover ? "✓ Worldwide 24x7" : "India only",
    accidental_death: getCover("accidental-death") ? `✓ ${getCover("accidental-death").payout}` : "✗ Not Covered",
    ptd: getCover("ptd") ? `✓ ${getCover("ptd").payout}` : "✗ Not Covered",
    ppd: getCover("ppd") ? `✓ ${getCover("ppd").payout}` : "✗ Not Covered",
    ttd: getCover("ttd") ? (getCover("ttd").optional ? "✓ Optional — weekly benefit like a salary" : "✓ Weekly benefit like a salary") : "✗ Not Covered",
    ttd_duration: getCover("ttd")?.payout || "N/A",
    child_education: getBenefit("education") ? `✓ ${getBenefit("education").limit}` : "✗ Not Available",
    hospital_cash: getBenefit("hospital cash") ? `✓ ${getBenefit("hospital cash").limit}` : (hasOptional("daily allowance") || hasOptional("hospital cash")) ? "✓ Optional cover" : "✗ Not Available",
    medical_expenses: getBenefit("medical expenses") ? "✓ Covered (Global option available)" : hasOptional("hospitalization expenses") ? "✓ Optional cover" : "✗ Not Available",
    air_ambulance: (() => {
      const b = plan.additional_benefits?.find(x => /evacuation|ambulance|air/.test(x.name?.toLowerCase() || ""));
      return b ? `✓ ${b.name}` : "✗ Not Available";
    })(),
    broken_bones: (plan.broken_bones_table || hasOptional("bones")) ? "✓ Optional — fixed payout table" : getBenefit("fracture") ? `✓ Built-in — ${getBenefit("fracture").limit}` : "✗ Not Available",
    burns: (plan.burns_table || hasOptional("burns")) ? "✓ Optional — fixed payout table" : "✗ Not Available",
    premium_basis: plan.premium_info?.note ? "Occupation, city & sum insured (not health)" : "Sum insured & risk class",
    payment_modes: plan.premium_info?.payment_modes || plan.policy_options?.premium_payment || "N/A",
    free_look: plan.policy_care?.free_look ? "15 days" : "N/A",
    grace_period: plan.policy_care?.grace_period ? "30 days" : "N/A",
    renewal: plan.policy_care?.renewal ? "✓ Lifelong renewability" : "N/A",
    claim_settlement_ratio: plan.irdai_data?.csr ? `${plan.irdai_data.csr}%` : "N/A",
    settlement_time: plan.claim_settlement_time || "Within 30 days of final documents",
    waiting_period: "Day 1 cover — no waiting period for accidents",
  };
};

// Helper function to extract comparison data from plan
const extractComparisonData = (plan) => {
  if (!plan) return {};
  
  const getRoomRent = () => {
    const baseRoomRent = plan.coverage_details?.hospitalization?.room_rent || "Covered up to SI";
    if (plan.id === 'icici-elevate') {
      return `${baseRoomRent} (Any Room Category available as modifier)`;
    }
    const roomModifier = plan.riders?.find(r => 
      r.name.toLowerCase().includes('room') || r.description?.toLowerCase().includes('room')
    );
    if (roomModifier) return `${baseRoomRent} (${roomModifier.name} available)`;
    return baseRoomRent;
  };
  
  const getAirAmbulance = () => {
    const baseAir = plan.coverage_details?.ambulance?.air || "Not Covered";
    const airRider = plan.riders?.find(r => r.name.toLowerCase().includes('air ambulance'));
    if (airRider) return `${baseAir} (via ${airRider.name})`;
    return baseAir;
  };
  
  const getRoadAmbulance = () => plan.coverage_details?.ambulance?.road || "Covered";
  
  const getMaternity = () => {
    const maternityRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('maternity') || r.name.toLowerCase().includes('parenthood')
    );
    if (maternityRider) {
      const waitingPeriod = maternityRider.waiting_period || "24 months";
      return `Optional: ${maternityRider.name} (WP: ${waitingPeriod})`;
    }
    return "Not Covered";
  };
  
  const getOPD = () => {
    const opdRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('opd') || r.name.toLowerCase().includes('wellbeing') || r.name.toLowerCase().includes('befit')
    );
    if (opdRider) return `Optional: ${opdRider.name}`;
    return "Not Covered";
  };
  
  const getConsumables = () => {
    const consumableRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('consumable') || r.name.toLowerCase().includes('claim shield') || r.name.toLowerCase().includes('claim protector')
    );
    if (consumableRider) return `Covered via ${consumableRider.name}`;
    if (plan.coverage_details?.consumables) return plan.coverage_details.consumables;
    return "Covered up to SI";
  };
  
  const getRestoreBenefit = () => {
    const restoreBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('restore') || b.name.toLowerCase().includes('recharge') ||
      b.name.toLowerCase().includes('reset') || b.name.toLowerCase().includes('secure')
    );
    if (restoreBenefit) {
      const detail = restoreBenefit.details?.[0] || restoreBenefit.description;
      return detail || "Available";
    }
    const restoreRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('restore') || r.name.toLowerCase().includes('recharge') || r.name.toLowerCase().includes('unlimited care')
    );
    if (restoreRider) return `Optional: ${restoreRider.name}`;
    return "Available";
  };
  
  const getNCB = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative') || b.name.toLowerCase().includes('loyalty')
    );
    if (ncbBenefit) {
      const detail = ncbBenefit.details?.[0] || ncbBenefit.description;
      return detail || "50% per year up to 100%";
    }
    return "50% per year up to 100%";
  };
  
  const getNCBReduction = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative')
    );
    if (ncbBenefit?.details) {
      const reduction = ncbBenefit.details.find(d => 
        d.toLowerCase().includes('reduce') || d.toLowerCase().includes('claim') || d.toLowerCase().includes('maintain')
      );
      return reduction || "May reduce on claim";
    }
    return "May reduce on claim";
  };
  
  const getPEDModification = () => {
    const pedInfo = plan.waiting_periods?.pre_existing?.reduction;
    const pedRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('ped') || r.name.toLowerCase().includes('jumpstart') || r.name.toLowerCase().includes('pre-existing')
    );
    if (pedRider) return `${pedInfo || 'Standard'} (${pedRider.name} available)`;
    return pedInfo || "Standard (36 months)";
  };
  
  const getHealthCheckup = () => {
    if (plan.health_checkup) {
      const limits = plan.health_checkup.limits_individual || plan.health_checkup.limits_floater;
      if (limits) {
        const firstKey = Object.keys(limits)[0];
        const firstLimit = limits[firstKey];
        return `${firstLimit}`;
      }
      if (plan.health_checkup.availability) return plan.health_checkup.availability;
    }
    return "Covered as per plan";
  };
  
  const getHospitalCash = () => {
    const hcRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('hospital cash') || r.name.toLowerCase().includes('daily cash')
    );
    if (hcRider) return `Optional: ${hcRider.name}`;
    return "Not Available";
  };
  
  const getCriticalIllness = () => {
    const ciRider = plan.riders?.find(r => r.name.toLowerCase().includes('critical illness'));
    if (ciRider) {
      const limit = ciRider.details?.find(d => d.includes('₹') || d.includes('Lakh'));
      return `Optional: ${ciRider.name}${limit ? ` (${limit})` : ''}`;
    }
    return "Not Covered";
  };
  
  const getAYUSHPrePost = () => {
    const ayushInfo = plan.coverage_details?.ayush;
    if (ayushInfo?.pre_post) return ayushInfo.pre_post;
    if (ayushInfo?.type?.toLowerCase().includes('in-patient') || ayushInfo?.type?.toLowerCase().includes('inpatient')) {
      return "Inpatient Only";
    }
    return "Inpatient Only";
  };
  
  const getMultiYearDiscount = () => {
    if (!plan.discounts) return "7.5% (2yr) / 10% (3yr)";
    // Handle both object and string discount formats
    const discount = plan.discounts.find(d => {
      const name = typeof d === 'string' ? d : d?.name;
      return name?.toLowerCase().includes('multi') || name?.toLowerCase().includes('long') || name?.toLowerCase().includes('tenure');
    });
    if (discount) {
      if (typeof discount === 'string') return discount;
      return discount.description || discount.discount || "7.5% (2yr) / 10% (3yr)";
    }
    return "7.5% (2yr) / 10% (3yr)";
  };

  // Helper to find discount by keyword (handles both string and object formats)
  const findDiscount = (keyword) => {
    if (!plan.discounts) return "Available";
    const discount = plan.discounts.find(d => {
      const name = typeof d === 'string' ? d : d?.name;
      return name?.toLowerCase().includes(keyword);
    });
    if (discount) {
      if (typeof discount === 'string') return discount;
      return discount.discount || discount.description || "Available";
    }
    return "Available";
  };
  
  const data = {
    sum_insured_display: plan.sum_insured_options?.slice(0, 5).join(", ") + (plan.sum_insured_options?.length > 5 ? "..." : "") || "N/A",
    room_rent: getRoomRent(),
    icu_limit: plan.coverage_details?.hospitalization?.icu_charges || plan.coverage_details?.hospitalization?.icu || "Covered up to SI",
    reasonable_customary_charges: plan.reasonable_customary_charges || "Applicable - Charges capped at prevailing rates",
    policy_tenure: plan.policy_options?.tenure?.join(", ") || "1, 2, 3 Years",
    family_floater: plan.policy_options?.max_members_floater || "Available",
    pre_hospitalization: plan.coverage_details?.pre_hospitalization?.days || "60 Days",
    post_hospitalization: plan.coverage_details?.post_hospitalization?.days || "180 Days",
    day_care: plan.coverage_details?.day_care?.procedures || "All Covered",
    domiciliary: plan.coverage_details?.domiciliary?.coverage || "Covered up to SI",
    organ_donor: plan.coverage_details?.organ_donor?.coverage || "Covered up to SI",
    road_ambulance: getRoadAmbulance(),
    air_ambulance: getAirAmbulance(),
    restore_benefit: getRestoreBenefit(),
    ncb: getNCB(),
    ncb_reduction: getNCBReduction(),
    ayush_coverage: plan.coverage_details?.ayush?.coverage || "Covered up to SI",
    ayush_pre_post: getAYUSHPrePost(),
    initial_waiting: plan.waiting_periods?.initial?.period || "30 Days",
    specific_disease_waiting: plan.waiting_periods?.specific_diseases?.period || "24 Months",
    pre_existing_waiting: plan.waiting_periods?.pre_existing?.period || "36 Months",
    ped_modification: getPEDModification(),
    health_checkup: getHealthCheckup(),
    consumables: getConsumables(),
    robotics: plan.coverage_details?.advanced_technology?.coverage || "Covered up to SI",
    second_opinion: "Available",
    maternity: getMaternity(),
    opd: getOPD(),
    hospital_cash: getHospitalCash(),
    critical_illness: getCriticalIllness(),
    copay_age: "No Co-pay",
    copay_zone: "No Zonal Co-pay",
    copay_room: "Proportionate deduction if room limit exceeded",
    sub_limits: getRoomRent().includes("No sub-limit") || getRoomRent().includes("No Capping") || getRoomRent().includes("At Actuals") ? "No Sub-limits" : "May have sub-limits",
    multi_year_discount: getMultiYearDiscount(),
    family_discount: findDiscount('family'),
    online_discount: findDiscount('online'),
  };
  
  const getCompanyId = (p) => {
    const company = p.company?.toLowerCase() || "";
    if (company.includes('hdfc')) return 'hdfc-ergo';
    if (company.includes('care')) return 'care-health';
    if (company.includes('icici')) return 'icici-lombard';
    if (company.includes('aditya') || company.includes('birla')) return 'aditya-birla';
    if (company.includes('manipal') || company.includes('cigna')) return 'manipal-cigna';
    if (company.includes('niva') || company.includes('bupa')) return 'niva-bupa';
    if (company.includes('tata') && !company.includes('aia')) return 'tata-aig';
    if (company.includes('star')) return 'star-health';
    if (company.includes('bajaj')) return 'bajaj-allianz';
    if (company.includes('digit') || company.includes('go digit')) return 'digit';
    if (company.includes('new india')) return 'new-india';
    if (company.includes('sbi')) return 'sbi-general';
    if (company.includes('reliance') && company.includes('general')) return 'reliance-general';
    if (company.includes('royal') || company.includes('sundaram')) return 'royal-sundaram';
    if (company.includes('future') || company.includes('generali')) return 'future-generali';
    if (company.includes('cholamandalam') || company.includes('chola')) return 'cholamandalam';
    if (company.includes('iffco') || company.includes('tokio')) return 'iffco-tokio';
    if (company.includes('acko')) return 'acko';
    return null;
  };
  
  const companyId = getCompanyId(plan);
  if (companyId && CLAIM_EXPERIENCE_DATA[companyId]) {
    const claimData = CLAIM_EXPERIENCE_DATA[companyId];
    data.claim_settlement_ratio = claimData.claim_settlement_ratio;
    data.incurred_claim_ratio = claimData.incurred_claim_ratio;
    data.icr_previous_year = claimData.icr_previous_year;
    data.icr_trend = claimData.icr_trend;
    data.solvency_ratio = claimData.solvency_ratio;
    data.complaints_per_10k = claimData.complaints_per_10k;
    data.gross_premium_cr = claimData.gross_premium_cr;
    data.network_hospitals = claimData.network_hospitals;
    data.cashless_hospitals = claimData.cashless_hospitals;
    data.irdai_health_category = claimData.irdai_health_category;
  }
  
  return data;
};

// Helper function to extract comparison data for Term/Life insurance plans (Beshak-style)
const extractTermComparisonData = (plan) => {
  if (!plan) return {};
  
  // Helper to check if feature/variant exists
  const hasFeature = (keywords) => {
    const allText = JSON.stringify(plan).toLowerCase();
    return keywords.some(k => allText.includes(k.toLowerCase()));
  };
  
  const getYesNo = (condition) => condition ? "✓ Yes" : "✗ No";
  const getAvailable = (condition) => condition ? "✓ Available" : "✗ Not Available";
  
  // Plan Information
  const getPlanType = () => {
    const types = [];
    if (plan.variants?.some(v => v.name.toLowerCase().includes('life secure') && !v.name.toLowerCase().includes('plus'))) types.push("Level Term");
    if (plan.variants?.some(v => v.name.toLowerCase().includes('plus'))) types.push("ROP");
    if (plan.variants?.some(v => v.name.toLowerCase().includes('joint'))) types.push("Joint Life");
    return types.length > 0 ? types.join(", ") : "Term Insurance";
  };
  
  const getSumAssuredRange = () => {
    if (plan.sum_insured_options || plan.sum_assured_options) {
      const options = plan.sum_insured_options || plan.sum_assured_options;
      return `${options[0]} - ${options[options.length - 1]}`;
    }
    return "₹25 Lakhs - No Limit";
  };
  
  // Death Benefit Options
  const hasLevelCover = () => {
    return plan.variants?.some(v => 
      v.name.toLowerCase().includes('secure') || 
      v.name.toLowerCase().includes('life')
    ) || true;
  };
  
  const hasIncreasingCover = () => {
    return hasFeature(['increasing cover', 'increasing sa', 'top-up', 'top up sa']) ||
           plan.coverage_details?.top_up_sa;
  };
  
  const hasDecreasingCover = () => {
    return hasFeature(['decreasing cover', 'loan cover', 'reducing']);
  };
  
  const hasWholeLifeOption = () => {
    return hasFeature(['whole life', 'whole of life', '99 years', 'lifetime']);
  };
  
  const hasLumpSumPayout = () => {
    const payoutOptions = plan.coverage_details?.death_benefit?.payout_options || "";
    return payoutOptions.toLowerCase().includes('lump') || true;
  };
  
  const hasStaggeredPayout = () => {
    const payoutOptions = plan.coverage_details?.death_benefit?.payout_options || "";
    return payoutOptions.toLowerCase().includes('staggered') || 
           payoutOptions.toLowerCase().includes('income') ||
           hasFeature(['dependent care', 'income payout']);
  };
  
  const hasLumpPlusIncome = () => {
    const payoutOptions = plan.coverage_details?.death_benefit?.payout_options || "";
    return payoutOptions.toLowerCase().includes('combination') ||
           hasFeature(['lump sum + income', 'combination']);
  };
  
  const hasAccidentalDeathBenefit = () => {
    return plan.core_benefits?.some(b => b.name.toLowerCase().includes('accidental')) ||
           plan.riders?.some(r => r.name.toLowerCase().includes('accidental death')) ||
           hasFeature(['accidental death']);
  };
  
  // Flexibility Options
  const hasRegularPay = () => {
    return plan.policy_options?.premium_payment?.toLowerCase().includes('regular') || true;
  };
  
  const hasLimitedPay = () => {
    return plan.policy_options?.premium_payment?.toLowerCase().includes('limited') ||
           hasFeature(['limited pay']);
  };
  
  const hasSinglePay = () => {
    return plan.policy_options?.premium_payment?.toLowerCase().includes('single') ||
           hasFeature(['single pay', 'single premium']);
  };
  
  const hasRollingLimitedPay = () => {
    return hasFeature(['rolling limited', 'choose exact years', 'flexible ppt']);
  };
  
  const hasPremiumBreak = () => {
    return plan.key_features?.some(f => 
      f.name.toLowerCase().includes('flexi') || 
      f.name.toLowerCase().includes('break') ||
      f.name.toLowerCase().includes('holiday')
    );
  };
  
  const hasEarlyExitBenefit = () => {
    return plan.key_features?.some(f => 
      f.name.toLowerCase().includes('exit') || 
      f.name.toLowerCase().includes('retirement') ||
      f.name.toLowerCase().includes('super')
    ) || hasFeature(['early exit', 'smart exit']);
  };
  
  const hasReturnOfPremium = () => {
    return plan.variants?.some(v => v.name.toLowerCase().includes('plus')) ||
           plan.coverage_details?.maturity_benefit?.benefit ||
           hasFeature(['return of premium', 'rop', 'maturity benefit']);
  };
  
  const hasCoverContinuation = () => {
    return plan.key_features?.some(f => 
      f.name.toLowerCase().includes('continuation')
    ) || hasFeature(['cover continuation']);
  };
  
  // Discounts
  const getWomenDiscount = () => {
    const disc = plan.discounts?.find(d => d.name.toLowerCase().includes('women'));
    return disc ? disc.discount : "✗ Not Available";
  };
  
  const getHighSADiscount = () => {
    const disc = plan.discounts?.find(d => 
      d.name.toLowerCase().includes('sum assured') || d.name.toLowerCase().includes('high')
    );
    return disc ? `✓ ${disc.discount}` : "✓ For SA ≥ ₹1 Cr";
  };
  
  const getOnlineDiscount = () => {
    const disc = plan.discounts?.find(d => d.name.toLowerCase().includes('online'));
    return disc ? `✓ ${disc.discount}` : "✗ Not Available";
  };
  
  const getAnnualModeDiscount = () => {
    return "✓ Available";
  };
  
  const getEmployeeDiscount = () => {
    const disc = plan.discounts?.find(d => 
      d.name.toLowerCase().includes('staff') || d.name.toLowerCase().includes('employee')
    );
    return disc ? `✓ ${disc.discount}` : "✓ Available";
  };
  
  const getHealthyLifestyleDiscount = () => {
    const disc = plan.discounts?.find(d => 
      d.name.toLowerCase().includes('healthy') || d.name.toLowerCase().includes('lifestyle')
    );
    return disc ? `✓ ${disc.discount}` : "✗ Not Available";
  };
  
  // Riders
  const getCriticalIllnessRider = () => {
    const rider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('critical illness') || 
      r.name.toLowerCase().includes('comprehensive')
    );
    return rider ? `✓ ${rider.name}` : "✓ Available via Rider";
  };
  
  const getCIConditionsCount = () => {
    const riderDesc = JSON.stringify(plan.riders || []).toLowerCase();
    if (riderDesc.includes('63')) return "63 Conditions";
    if (riderDesc.includes('64')) return "64 Conditions";
    if (riderDesc.includes('45')) return "45 Conditions";
    if (riderDesc.includes('36')) return "36 Conditions";
    return "34+ Conditions";
  };
  
  const getAccidentalDeathRider = () => {
    const rider = plan.riders?.find(r => r.name.toLowerCase().includes('accidental death'));
    return rider ? `✓ ${rider.name}` : "✓ Available via Rider";
  };
  
  const getAccidentalDisabilityRider = () => {
    const rider = plan.riders?.find(r => r.name.toLowerCase().includes('disability'));
    return rider ? `✓ ${rider.name}` : "✓ Available via Rider";
  };
  
  const getWaiverOfPremiumRider = () => {
    const rider = plan.riders?.find(r => r.name.toLowerCase().includes('waiver'));
    const coreBenefit = plan.core_benefits?.find(b => b.name.toLowerCase().includes('waiver'));
    if (coreBenefit) return `✓ Built-in: ${coreBenefit.name}`;
    return rider ? `✓ ${rider.name}` : "✓ Available via Rider";
  };
  
  const getTerminalIllnessBenefit = () => {
    const tiBenefit = plan.core_benefits?.find(b => b.name.toLowerCase().includes('terminal'));
    if (tiBenefit) return `✓ ${plan.coverage_details?.terminal_illness?.acceleration || '50% SA Accelerated'}`;
    return "✓ 50% SA Accelerated";
  };
  
  // Additional Benefits
  const getSpouseCover = () => {
    const variant = plan.variants?.find(v => v.name.toLowerCase().includes('joint'));
    if (variant) return `✓ ${variant.name}`;
    const rider = plan.riders?.find(r => r.name.toLowerCase().includes('spouse'));
    return rider ? `✓ ${rider.name}` : "✗ Not Available";
  };
  
  const getJointLifeCover = () => {
    const variant = plan.variants?.find(v => v.name.toLowerCase().includes('joint'));
    return variant ? `✓ ${variant.name}` : "✗ Not Available";
  };
  
  const getChildEducationBenefit = () => {
    const feature = plan.key_features?.find(f => 
      f.name.toLowerCase().includes('education') || f.name.toLowerCase().includes('child')
    );
    const rider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('child') || r.name.toLowerCase().includes('education')
    );
    if (feature) return `✓ ${feature.name}`;
    if (rider) return `✓ ${rider.name}`;
    return "✗ Not Available";
  };
  
  const getLifeStageBenefit = () => {
    const feature = plan.key_features?.find(f => f.name.toLowerCase().includes('life stage'));
    return feature ? `✓ ${feature.description?.substring(0, 50)}...` : "✗ Not Available";
  };
  
  const getWellnessBenefits = () => {
    if (plan.wellness_benefits?.features) {
      return `✓ ${plan.wellness_benefits.platform || 'Health Services'}`;
    }
    return "✗ Not Available";
  };
  
  const getInstantClaimPayout = () => {
    const feature = plan.key_features?.find(f => f.name.toLowerCase().includes('instant'));
    if (feature) return `✓ ${feature.description?.substring(0, 50)}...`;
    return "✗ Not Available";
  };
  
  // Build the data object
  const data = {
    // Plan Information
    plan_type: getPlanType(),
    uin: plan.uin || "N/A",
    sum_assured_range: getSumAssuredRange(),
    entry_age: plan.policy_options?.entry_age || "18-65 years",
    maturity_age: plan.policy_options?.maturity_age || "Up to 85 years",
    policy_term: plan.policy_options?.policy_term || "5-40 years",
    
    // Death Benefit
    level_cover: getYesNo(hasLevelCover()),
    increasing_cover: getYesNo(hasIncreasingCover()),
    decreasing_cover: getYesNo(hasDecreasingCover()),
    whole_life_option: getYesNo(hasWholeLifeOption()),
    payout_lump_sum: getYesNo(hasLumpSumPayout()),
    payout_staggered: getYesNo(hasStaggeredPayout()),
    payout_lump_plus_income: getYesNo(hasLumpPlusIncome()),
    accidental_death_benefit: getYesNo(hasAccidentalDeathBenefit()),
    
    // Flexibility
    regular_pay: getYesNo(hasRegularPay()),
    limited_pay: getYesNo(hasLimitedPay()),
    single_pay: getYesNo(hasSinglePay()),
    rolling_limited_pay: getYesNo(hasRollingLimitedPay()),
    premium_break: getAvailable(hasPremiumBreak()),
    early_exit_benefit: getAvailable(hasEarlyExitBenefit()),
    return_of_premium: getAvailable(hasReturnOfPremium()),
    cover_continuation: getAvailable(hasCoverContinuation()),
    
    // Discounts
    women_discount: getWomenDiscount(),
    high_sa_discount: getHighSADiscount(),
    online_discount: getOnlineDiscount(),
    annual_mode_discount: getAnnualModeDiscount(),
    employee_discount: getEmployeeDiscount(),
    healthy_lifestyle_discount: getHealthyLifestyleDiscount(),
    
    // Riders
    critical_illness_rider: getCriticalIllnessRider(),
    ci_conditions_count: getCIConditionsCount(),
    accidental_death_rider: getAccidentalDeathRider(),
    accidental_disability_rider: getAccidentalDisabilityRider(),
    waiver_of_premium: getWaiverOfPremiumRider(),
    terminal_illness_benefit: getTerminalIllnessBenefit(),
    
    // Additional Benefits
    spouse_cover: getSpouseCover(),
    joint_life_cover: getJointLifeCover(),
    child_education_benefit: getChildEducationBenefit(),
    life_stage_benefit: getLifeStageBenefit(),
    wellness_benefits: getWellnessBenefits(),
    instant_claim_payout: getInstantClaimPayout(),
  };
  
  // Add IRDAI claim experience data
  const getLifeCompanyId = (p) => {
    const company = p.company?.toLowerCase() || "";
    if (company.includes('hdfc') && company.includes('life')) return 'hdfc-life';
    if (company.includes('tata') && company.includes('aia')) return 'tata-aia-life';
    return null;
  };
  
  const companyId = getLifeCompanyId(plan);
  if (companyId && LIFE_CLAIM_EXPERIENCE_DATA[companyId]) {
    const claimData = LIFE_CLAIM_EXPERIENCE_DATA[companyId];
    data.claim_settlement_ratio = claimData.claim_settlement_ratio;
    data.csr_previous_year = claimData.csr_previous_year;
    data.claims_settled_in_30_days = claimData.settlement_within_30_days;
    data.claim_rejection_rate = `${(100 - parseFloat(claimData.claim_settlement_ratio)).toFixed(2)}%`;
    data.grievance_ratio = "Low";
    data.irdai_category = claimData.irdai_category;
  }
  
  return data;
};

export default function ComparePlans() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(["plan_info", "death_benefit"]);
  const [showRiders, setShowRiders] = useState(false);
  const [activePlanIndex, setActivePlanIndex] = useState(0); // For mobile swipe
  const [isDownloading, setIsDownloading] = useState(false);
  const isMobile = useIsMobile();
  const comparisonRef = useRef(null);

  // PDF Download function - Creates a clean, professional comparison PDF using AutoTable
  const downloadPDF = async () => {
    if (plans.length < 2) return;
    
    setIsDownloading(true);
    
    // Track PDF generation in Google Analytics
    const planNamesForTracking = plans.map(p => p.name);
    const planType = isComparingPAPlans() ? 'Personal Accident Insurance' : isComparingTermPlans() ? 'Term Insurance' : 'Health Insurance';
    trackPDFGeneration(planType, planNamesForTracking);
    
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      
      // Professional color palette
      const primaryColor = [31, 41, 55];      // Dark slate
      const accentColor = [234, 179, 8];      // Amber/Gold
      const greenColor = [22, 163, 74];       // Green
      const redColor = [220, 38, 38];         // Red
      const lightGray = [249, 250, 251];      // Very light gray
      const mediumGray = [107, 114, 128];     // Medium gray
      
      // Extract comparison data
      const comparisonData = plans.map(plan => extractComparisonData(plan));
      
      // ========== HEADER ==========
      // Header background
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 42, 'F');
      
      // Accent line
      pdf.setFillColor(...accentColor);
      pdf.rect(0, 42, pageWidth, 2, 'F');
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Health Insurance Plan Comparison', pageWidth / 2, 18, { align: 'center' });
      
      // Subtitle with plan names
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const planNamesText = plans.map(p => p.name).join('  vs  ');
      const displayNames = planNamesText.length > 65 ? planNamesText.substring(0, 62) + '...' : planNamesText;
      pdf.text(displayNames, pageWidth / 2, 28, { align: 'center' });
      
      // Date
      pdf.setFontSize(9);
      pdf.setTextColor(200, 200, 200);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageWidth / 2, 37, { align: 'center' });
      
      let yPos = 52;
      
      // ========== PLAN OVERVIEW CARDS ==========
      const cardWidth = (pageWidth - margin * 2 - 8) / plans.length;
      
      plans.forEach((plan, idx) => {
        const x = margin + idx * (cardWidth + 4);
        
        // Card background
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(x, yPos, cardWidth, 32, 2, 2, 'FD');
        
        // Color indicator bar
        const barColor = idx === 0 ? [59, 130, 246] : [16, 185, 129];
        pdf.setFillColor(...barColor);
        pdf.rect(x, yPos, cardWidth, 6, 'F');
        pdf.setFillColor(...barColor);
        pdf.roundedRect(x, yPos, cardWidth, 6, 2, 2, 'F');
        pdf.rect(x, yPos + 3, cardWidth, 3, 'F');
        
        // Plan name
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        const shortName = plan.name.length > 25 ? plan.name.substring(0, 22) + '...' : plan.name;
        pdf.text(shortName, x + cardWidth / 2, yPos + 4.5, { align: 'center' });
        
        // Company name
        pdf.setTextColor(...mediumGray);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        const companyName = (plan.company || '').substring(0, 30);
        pdf.text(companyName, x + cardWidth / 2, yPos + 13, { align: 'center' });
        
        // Sum Insured
        pdf.setTextColor(...primaryColor);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        const siRange = plan.sum_insured_range || plan.sum_insured_options?.slice(0, 2).join(' - ') || 'Variable';
        pdf.text(siRange, x + cardWidth / 2, yPos + 21, { align: 'center' });
        
        // CSR Badge
        const csr = comparisonData[idx].claim_settlement_ratio || 'N/A';
        pdf.setFillColor(240, 253, 244);
        pdf.roundedRect(x + 8, yPos + 24, cardWidth - 16, 6, 1, 1, 'F');
        pdf.setTextColor(...greenColor);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`CSR: ${csr}`, x + cardWidth / 2, yPos + 28, { align: 'center' });
      });
      
      yPos += 40;
      
      // ========== COMPARISON TABLES USING AUTOTABLE ==========
      
      // Helper to format cell value with color
      const formatValue = (val) => {
        const str = String(val || 'N/A');
        return str;
      };
      
      // Helper to get cell color
      const getCellColor = (val) => {
        const str = String(val || '').toLowerCase();
        if (str.includes('n/a') || str === 'not covered' || str === 'no') {
          return redColor;
        } else if (str.includes('covered') || str.includes('no cap') || str.includes('unlimited') || 
                   str === 'nil' || str === '0%' || str === 'yes' || str.includes('included')) {
          return greenColor;
        }
        return primaryColor;
      };
      
      // Define all comparison sections
      const sections = [
        {
          title: 'Claim Experience (IRDAI Data)',
          features: [
            { key: 'claim_settlement_ratio', label: 'Claim Settlement Ratio' },
            { key: 'incurred_claim_ratio', label: 'Incurred Claim Ratio' },
            { key: 'network_hospitals', label: 'Network Hospitals' },
          ]
        },
        {
          title: 'Room & Hospitalization',
          features: [
            { key: 'room_rent', label: 'Room Rent' },
            { key: 'icu_charges', label: 'ICU Charges' },
            { key: 'pre_hospitalization', label: 'Pre-Hospitalization' },
            { key: 'post_hospitalization', label: 'Post-Hospitalization' },
            { key: 'day_care', label: 'Day Care' },
          ]
        },
        {
          title: 'Ambulance Coverage',
          features: [
            { key: 'road_ambulance', label: 'Road Ambulance' },
            { key: 'air_ambulance', label: 'Air Ambulance' },
          ]
        },
        {
          title: 'Waiting Periods',
          features: [
            { key: 'initial_waiting', label: 'Initial Waiting' },
            { key: 'pre_existing_waiting', label: 'Pre-Existing Disease' },
            { key: 'specific_disease_waiting', label: 'Specific Disease' },
          ]
        },
        {
          title: 'Bonus & Restore Benefits',
          features: [
            { key: 'restore_benefit', label: 'Restore Benefit' },
            { key: 'ncb', label: 'No Claim Bonus' },
          ]
        },
        {
          title: 'Additional Benefits',
          features: [
            { key: 'consumables', label: 'Consumables' },
            { key: 'health_checkup', label: 'Health Checkup' },
            { key: 'ayush_coverage', label: 'AYUSH Treatment' },
          ]
        },
        {
          title: 'Optional Add-ons',
          features: [
            { key: 'maternity', label: 'Maternity' },
            { key: 'opd', label: 'OPD Coverage' },
            { key: 'critical_illness', label: 'Critical Illness' },
          ]
        },
        {
          title: 'Co-payments',
          features: [
            { key: 'copay_age', label: 'Age Co-pay' },
            { key: 'copay_zone', label: 'Zone Co-pay' },
          ]
        },
      ];
      
      // Create tables for each section
      sections.forEach((section) => {
        // Prepare table data
        const headers = ['Feature', ...plans.map(p => p.name.substring(0, 18))];
        const body = section.features.map(feature => {
          const row = [feature.label];
          comparisonData.forEach(data => {
            row.push(formatValue(data[feature.key]));
          });
          return row;
        });
        
        // Add section table
        autoTable(pdf, {
          startY: yPos,
          head: [[{ content: section.title, colSpan: headers.length, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', halign: 'left' } }]],
          body: body,
          columns: headers.map((h, i) => ({ header: h, dataKey: i })),
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 3,
            lineColor: [229, 231, 235],
            lineWidth: 0.3,
          },
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 40, fontStyle: 'bold', textColor: primaryColor },
          },
          alternateRowStyles: {
            fillColor: lightGray,
          },
          didParseCell: (data) => {
            // Color code values in body cells (not header or first column)
            if (data.section === 'body' && data.column.index > 0) {
              const cellColor = getCellColor(data.cell.raw);
              data.cell.styles.textColor = cellColor;
            }
          },
          margin: { left: margin, right: margin },
        });
        
        yPos = pdf.lastAutoTable.finalY + 6;
        
        // Check for page break
        if (yPos > pageHeight - 50) {
          pdf.addPage();
          yPos = 15;
        }
      });
      
      // ========== RIDERS TABLE ==========
      const allRiders = new Set();
      plans.forEach(plan => {
        plan.riders?.forEach(r => allRiders.add(r.name));
      });
      
      if (allRiders.size > 0) {
        const riderHeaders = ['Rider', ...plans.map(p => p.name.substring(0, 18))];
        const riderBody = Array.from(allRiders).slice(0, 10).map(riderName => {
          const row = [riderName.substring(0, 35)];
          plans.forEach(plan => {
            const hasRider = plan.riders?.some(r => r.name === riderName);
            row.push(hasRider ? 'Available' : 'Not Available');
          });
          return row;
        });
        
        autoTable(pdf, {
          startY: yPos,
          head: [[{ content: 'Available Riders & Add-ons', colSpan: riderHeaders.length, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', halign: 'left' } }]],
          body: riderBody,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 3,
            lineColor: [229, 231, 235],
            lineWidth: 0.3,
          },
          columnStyles: {
            0: { cellWidth: 50, fontStyle: 'bold', textColor: primaryColor },
          },
          alternateRowStyles: {
            fillColor: lightGray,
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index > 0) {
              const val = String(data.cell.raw).toLowerCase();
              if (val.includes('available') && !val.includes('not')) {
                data.cell.styles.textColor = greenColor;
              } else {
                data.cell.styles.textColor = redColor;
              }
            }
          },
          margin: { left: margin, right: margin },
        });
        
        yPos = pdf.lastAutoTable.finalY + 8;
      }
      
      // ========== DISCLAIMER ==========
      if (yPos > pageHeight - 45) {
        pdf.addPage();
        yPos = 15;
      }
      
      // Disclaimer box
      pdf.setFillColor(254, 252, 232);
      pdf.setDrawColor(...accentColor);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 20, 2, 2, 'FD');
      
      pdf.setTextColor(...accentColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Important Disclaimer', margin + 4, yPos + 6);
      
      pdf.setTextColor(...primaryColor);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('This comparison is for informational purposes only. Features are subject to policy terms.', margin + 4, yPos + 12);
      pdf.text('Please read official policy documents before making any decision.', margin + 4, yPos + 17);
      
      yPos += 26;
      
      // ========== CONTACT CTA ==========
      pdf.setFillColor(...accentColor);
      pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 14, 2, 2, 'F');
      
      pdf.setTextColor(...primaryColor);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Need Help? Get FREE Expert Advice!', pageWidth / 2, yPos + 6, { align: 'center' });
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Call/WhatsApp: +91 9510020907', pageWidth / 2, yPos + 11, { align: 'center' });
      
      // ========== FOOTER ON ALL PAGES ==========
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Footer bar
        pdf.setFillColor(...primaryColor);
        pdf.rect(0, pageHeight - 10, pageWidth, 10, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.text(`Page ${i} of ${totalPages}  |  Kavach Box by Prashant K Uunadkat  |  www.prashantkuunadkat.com`, pageWidth / 2, pageHeight - 4, { align: 'center' });
      }
      
      // Save PDF
      const filename = plans.map(p => p.name.replace(/\s+/g, '-').substring(0, 15)).join('_vs_');
      pdf.save(`Insurance-Comparison-${filename}.pdf`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    fetchAllPlans();
  }, []);

  useEffect(() => {
    const planIds = searchParams.get('plans');
    if (planIds && allPlans.length > 0) {
      const ids = planIds.split(',');
      const matchedPlans = ids.map(id => allPlans.find(p => p.id === id)).filter(Boolean);
      if (matchedPlans.length > 0) {
        setSelectedPlans(matchedPlans);
        fetchPlanDetails(matchedPlans.map(p => p.id));
      }
    }
  }, [searchParams, allPlans]);

  const fetchAllPlans = async () => {
    try {
      // Fetch all plans (the /api/plans endpoint now returns both health and term plans)
      const response = await axios.get(`${API}/plans`);
      setAllPlans(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching plans:", error);
      setLoading(false);
    }
  };

  // Helper to detect if comparing term/life insurance plans
  const isComparingTermPlans = () => {
    if (plans.length === 0) return false;
    return plans.some(plan => plan.type === 'term');
  };

  // Helper to detect if comparing personal accident plans
  const isComparingPAPlans = () => {
    if (plans.length === 0) return false;
    return plans.some(plan => plan.type === 'pa');
  };

  // Get the appropriate comparison categories based on plan type
  const getComparisonCategories = () => {
    if (isComparingPAPlans()) return PA_COMPARISON_CATEGORIES;
    return isComparingTermPlans() ? TERM_COMPARISON_CATEGORIES : COMPARISON_CATEGORIES;
  };

  // Get the appropriate data extraction function based on plan type
  const getComparisonData = (plan) => {
    if (plan.type === 'pa') return extractPAComparisonData(plan);
    return plan.type === 'term' ? extractTermComparisonData(plan) : extractComparisonData(plan);
  };

  const fetchPlanDetails = async (planIds) => {
    setLoading(true);
    try {
      const planPromises = planIds.map(id => axios.get(`${API}/plans/${id}`));
      const responses = await Promise.all(planPromises);
      const fetchedPlans = responses.map(r => r.data);
      setPlans(fetchedPlans);
      
      // Track comparison when 2+ plans are selected
      if (fetchedPlans.length >= 2) {
        trackComparison(fetchedPlans.map(p => p.name));
      }
    } catch (error) {
      console.error("Error fetching plan details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    if (selectedPlans.find(p => p.id === plan.id)) {
      const newSelection = selectedPlans.filter(p => p.id !== plan.id);
      setSelectedPlans(newSelection);
      setPlans(plans.filter(p => p.id !== plan.id));
    } else if (selectedPlans.length < 5) {
      setSelectedPlans([...selectedPlans, plan]);
      fetchPlanDetails([...selectedPlans.map(p => p.id), plan.id]);
    }
  };

  const removePlan = (planId) => {
    setSelectedPlans(selectedPlans.filter(p => p.id !== planId));
    setPlans(plans.filter(p => p.id !== planId));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const renderValue = (value, field, plan = null) => {
    if (!value || value === "N/A") {
      return <span className="text-[#3A4557]">N/A</span>;
    }
    
    const valueStr = String(value).toLowerCase();
    const isPositive = valueStr.includes('covered') || valueStr.includes('available') || 
                       valueStr.includes('unlimited') || valueStr.includes('no co-pay') ||
                       valueStr.includes('no sub-limit') || valueStr.includes('no capping');
    const isNegative = valueStr.includes('not covered') || valueStr.includes('not available') || valueStr.includes('excluded');
    
    // Wrap content in ClickableFeature if plan info is available
    const wrapWithClickable = (content) => {
      if (plan && field?.key) {
        return (
          <ClickableFeature
            featureKey={field.key}
            value={value}
            planId={plan.id}
            planName={plan.name}
            planDocuments={plan.documents}
            showIcon={false}
          >
            {content}
          </ClickableFeature>
        );
      }
      return content;
    };
    
    if (isPositive) {
      return (
        <span className="flex items-center justify-center gap-1 text-emerald-600">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {wrapWithClickable(<span className="text-sm">{value}</span>)}
        </span>
      );
    }
    
    if (isNegative) {
      return (
        <span className="flex items-center justify-center gap-1 text-red-600">
          <X className="h-4 w-4 flex-shrink-0" />
          {wrapWithClickable(<span className="text-sm">{value}</span>)}
        </span>
      );
    }
    
    return wrapWithClickable(<span className="text-[#2E3B57] text-sm">{value}</span>);
  };

  if (loading && allPlans.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3"
            title="Home"
          >
            <Home className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Back</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
              <Scale className="h-4 w-4 sm:h-5 sm:w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="font-bold text-[#1B2A4A] text-sm sm:text-base">Compare</h1>
              <p className="text-xs text-[#3A4557] hidden sm:block">Side-by-side comparison</p>
            </div>
          </div>
        </div>
        <a href="tel:+919510020907">
          <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm">
            <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Get Quote</span>
          </Button>
        </a>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-8">
        {/* Plan Selection */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 sm:p-6 mb-4 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-[#A87608]" />
              <h2 className="font-bold text-[#1B2A4A] text-sm sm:text-base">Select Plans</h2>
              <span className="text-[#3A4557] text-xs sm:text-sm">({selectedPlans.length}/5)</span>
            </div>
            <Button
              onClick={() => setShowPlanSelector(!showPlanSelector)}
              className="btn-outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Plan
            </Button>
          </div>

          {/* Selected Plans */}
          {selectedPlans.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4">
              {selectedPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="flex items-center gap-2 px-4 py-2 bg-[#EDF1F8]/40 border border-blue-500/30 rounded-xl"
                >
                  <img
                    src={plan.company_logo}
                    alt={plan.company}
                    className="h-6 w-6 object-contain bg-white rounded p-0.5"
                  />
                  <span className="text-[#1B2A4A] text-sm font-medium">{plan.name}</span>
                  <button
                    onClick={() => removePlan(plan.id)}
                    className="text-[#3A4557] hover:text-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Plan Selector Dropdown */}
          {showPlanSelector && (
            <div className="mt-4 max-h-[70vh] overflow-y-auto border border-[#C9D4E6] rounded-xl p-4 pb-6 bg-blue-900/30">
              {/* Health Insurance Plans Section */}
              {allPlans.filter(p => p.type !== 'term' && p.type !== 'pa').length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-emerald-500/40">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <Heart className="h-5 w-5 text-[#1B2A4A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-emerald-600">Health Insurance Plans</h3>
                    <p className="text-xs text-[#3A4557]">{allPlans.filter(p => p.type !== 'term' && p.type !== 'pa').length} plans available</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allPlans.filter(p => p.type !== 'term' && p.type !== 'pa').map((plan) => {
                    const isSelected = selectedPlans.find(p => p.id === plan.id);
                    return (
                      <button
                        key={plan.id}
                        onClick={() => handlePlanSelect(plan)}
                        disabled={selectedPlans.length >= 5 && !isSelected}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          isSelected
                            ? 'bg-[#D4A017]/15 border-[#D4A017]/50'
                            : 'bg-blue-900/30 border-emerald-500/20 hover:border-emerald-500/50'
                        } ${selectedPlans.length >= 5 && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`compare-health-plan-${plan.id}`}
                      >
                        <img
                          src={plan.company_logo}
                          alt={plan.company}
                          className="h-10 w-10 object-contain bg-white rounded-lg p-1"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${isSelected ? 'text-[#A87608]' : 'text-[#1B2A4A]'}`}>
                            {plan.name}
                          </p>
                          <p className="text-xs text-[#3A4557] truncate">{plan.company}</p>
                        </div>
                        {isSelected && <Check className="h-5 w-5 text-[#A87608]" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Term/Life Insurance Plans Section */}
              {allPlans.filter(p => p.type === 'term').length > 0 && (
                <div className="mt-8 pt-4 border-t border-[#C9D4E6]/50">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-500/40">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Shield className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#2E3B57]">Term / Life Insurance Plans</h3>
                      <p className="text-xs text-[#3A4557]">{allPlans.filter(p => p.type === 'term').length} plans available</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allPlans.filter(p => p.type === 'term').map((plan) => {
                      const isSelected = selectedPlans.find(p => p.id === plan.id);
                      return (
                        <button
                          key={plan.id}
                          onClick={() => handlePlanSelect(plan)}
                          disabled={selectedPlans.length >= 5 && !isSelected}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                            isSelected
                              ? 'bg-[#D4A017]/15 border-[#D4A017]/50'
                              : 'bg-blue-900/30 border-[#C9D4E6] hover:border-blue-500/50'
                          } ${selectedPlans.length >= 5 && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`compare-term-plan-${plan.id}`}
                        >
                          <img
                            src={plan.company_logo}
                            alt={plan.company}
                            className="h-10 w-10 object-contain bg-white rounded-lg p-1"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm truncate ${isSelected ? 'text-[#A87608]' : 'text-[#1B2A4A]'}`}>
                              {plan.name}
                            </p>
                            <p className="text-xs text-[#3A4557] truncate">{plan.company}</p>
                          </div>
                          {isSelected && <Check className="h-5 w-5 text-[#A87608]" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Personal Accident Plans Section */}
              {allPlans.filter(p => p.type === 'pa').length > 0 && (
                <div className="mt-8 pt-4 border-t border-[#C9D4E6]/50">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-amber-500/40">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <Activity className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-amber-600">Personal Accident Plans</h3>
                      <p className="text-xs text-[#3A4557]">{allPlans.filter(p => p.type === 'pa').length} plans available</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allPlans.filter(p => p.type === 'pa').map((plan) => {
                      const isSelected = selectedPlans.find(p => p.id === plan.id);
                      return (
                        <button
                          key={plan.id}
                          onClick={() => handlePlanSelect(plan)}
                          disabled={selectedPlans.length >= 5 && !isSelected}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                            isSelected
                              ? 'bg-[#D4A017]/15 border-[#D4A017]/50'
                              : 'bg-blue-900/30 border-amber-500/20 hover:border-amber-500/50'
                          } ${selectedPlans.length >= 5 && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`compare-pa-plan-${plan.id}`}
                        >
                          <img
                            src={plan.company_logo}
                            alt={plan.company}
                            className="h-10 w-10 object-contain bg-white rounded-lg p-1"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm truncate ${isSelected ? 'text-[#A87608]' : 'text-[#1B2A4A]'}`}>
                              {plan.name}
                            </p>
                            <p className="text-xs text-[#3A4557] truncate">{plan.company}</p>
                          </div>
                          {isSelected && <Check className="h-5 w-5 text-[#A87608]" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedPlans.length === 0 && !showPlanSelector && (
            <div className="text-center py-8">
              <Scale className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
              <p className="text-[#3A4557]">Click &quot;Add Plan&quot; to select plans for comparison</p>
            </div>
          )}
        </motion.div>

        {/* Comparison Table */}
        {selectedPlans.length >= 2 && plans.length >= 2 && (
          <>
            {/* Mobile Sticky Plan Header */}
            {isMobile && (
              <div className="sticky top-14 z-30 -mx-2 px-2 pt-1 pb-2 bg-[#F4F6FA]/95 backdrop-blur-sm">
                <div className="glass-card p-3 border-[#D4A017]/30">
                  <div className="flex items-center justify-between">
                    <button 
                      onClick={() => setActivePlanIndex(Math.max(0, activePlanIndex - 1))}
                      disabled={activePlanIndex === 0}
                      className={`p-1.5 rounded-lg ${activePlanIndex === 0 ? 'text-[#3A4557]' : 'text-[#1B2A4A] bg-[#EDF1F8]/40'}`}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    <div className="flex items-center gap-3 flex-1 justify-center">
                      <div className="h-10 w-10 rounded-lg bg-white p-1.5 flex-shrink-0">
                        <img
                          src={plans[activePlanIndex]?.company_logo}
                          alt={plans[activePlanIndex]?.company}
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <div className="text-left min-w-0">
                        <h3 className="font-bold text-[#1B2A4A] text-sm truncate">{plans[activePlanIndex]?.name}</h3>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {plans.map((plan, idx) => (
                              <button
                                key={plan.id}
                                onClick={() => setActivePlanIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === activePlanIndex ? 'bg-[#D4A017]' : 'bg-gray-600'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-[#3A4557]">{activePlanIndex + 1}/{plans.length}</span>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => setActivePlanIndex(Math.min(plans.length - 1, activePlanIndex + 1))}
                      disabled={activePlanIndex === plans.length - 1}
                      className={`p-1.5 rounded-lg ${activePlanIndex === plans.length - 1 ? 'text-[#3A4557]' : 'text-[#1B2A4A] bg-[#EDF1F8]/40'}`}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Download Button */}
            <div className="flex justify-end mb-4">
              <Button 
                onClick={downloadPDF}
                disabled={isDownloading}
                className="btn-outline text-sm"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>

            {/* Comparison Content - Wrapped for PDF */}
            <div ref={comparisonRef}>

            {/* Desktop Plan Headers - Sticky */}
            {!isMobile && (
              <div className="sticky top-16 z-30 -mx-2 px-2 pt-1 pb-2" style={{ backgroundColor: 'rgba(10, 22, 40, 0.97)', backdropFilter: 'blur(8px)' }}>
                <div className="glass-card p-4 border-[#D4A017]/30"><Swipeable hint>
                  <div 
                    className="grid gap-4 items-center"
                    style={{ 
                      gridTemplateColumns: selectedPlans.length >= 5 
                        ? `160px repeat(${selectedPlans.length}, minmax(160px, 1fr))`
                        : `200px repeat(${selectedPlans.length}, minmax(200px, 1fr))`,
                      minWidth: selectedPlans.length >= 5 ? `${160 + selectedPlans.length * 160}px` : 'auto'
                    }}
                  >
                    <div className="flex items-center">
                      <span className="font-bold text-[#1B2A4A] text-sm">Plan Details</span>
                    </div>
                    {plans.map((plan) => (
                      <div key={plan.id} className="flex items-center justify-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-white p-1.5 flex-shrink-0">
                          <img
                            src={plan.company_logo}
                            alt={plan.company}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div className="text-left min-w-0">
                          <h3 className="font-bold text-[#1B2A4A] text-sm truncate">{plan.name}</h3>
                          <p className="text-xs text-[#3A4557] truncate">{plan.company}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Swipeable></div>
              </div>
            )}

            {/* Comparison Categories */}
            {getComparisonCategories().map((category) => {
              const Icon = category.icon;
              const isExpanded = expandedCategories.includes(category.id);
              const comparisonData = plans.map(plan => ({
                plan,
                data: getComparisonData(plan)
              }));

              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card mb-4 overflow-hidden"
                >
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-[#EDF1F8]/30 transition-colors"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                        <Icon className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
                      </div>
                      <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">{category.name}</h2>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[#C9D4E6]">
                      {/* Mobile: Card-based view */}
                      {isMobile ? (
                        <div className="p-4 space-y-3">
                          {category.fields.map((field, fieldIndex) => (
                            <div
                              key={field.key}
                              className={`p-3 rounded-lg ${fieldIndex % 2 === 0 ? 'bg-blue-900/20' : 'bg-blue-900/10'}`}
                            >
                              <p className="text-[#3A4557] text-xs mb-2 font-medium">{field.label}</p>
                              <div className="text-[#1B2A4A] text-sm">
                                {renderValue(comparisonData[activePlanIndex]?.data[field.key], field, comparisonData[activePlanIndex]?.plan)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Desktop: Grid view */
                        <Swipeable hint>
                          <div style={{ minWidth: plans.length >= 5 ? `${160 + plans.length * 160}px` : '600px' }}>
                            {category.fields.map((field, fieldIndex) => (
                              <div
                                key={field.key}
                                className={`grid gap-4 p-4 ${fieldIndex % 2 === 0 ? 'bg-blue-900/20' : ''}`}
                                style={{ 
                                  gridTemplateColumns: plans.length >= 5
                                    ? `160px repeat(${plans.length}, minmax(160px, 1fr))`
                                    : `200px repeat(${plans.length}, minmax(200px, 1fr))`
                                }}
                              >
                                <div className="flex items-center">
                                  <span className="text-[#2E3B57] text-sm font-medium">{field.label}</span>
                                </div>
                                {comparisonData.map(({ plan, data }) => (
                                  <div key={plan.id} className="flex items-center justify-center text-center px-2">
                                    {renderValue(data[field.key], field, plan)}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </Swipeable>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}

            {/* Riders Comparison - By Category */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <button
                onClick={() => setShowRiders(!showRiders)}
                className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-[#EDF1F8]/30 transition-colors"
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
                  </div>
                  <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">Add-on Riders</h2>
                </div>
                {showRiders ? (
                  <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                )}
              </button>

              {showRiders && (
                <div className="border-t border-[#C9D4E6]">
                  {(() => {
                    // Define rider categories with keywords to match
                    const RIDER_CATEGORIES = [
                      { 
                        id: "consumables",
                        name: "Consumables Cover",
                        description: "Covers gloves, PPE, syringes, IV sets, etc.",
                        keywords: ["consumable", "claim shield", "claim protector", "non-medical", "claimshield"]
                      },
                      { 
                        id: "maternity",
                        name: "Maternity Cover",
                        description: "Covers childbirth, C-section expenses",
                        keywords: ["maternity", "parenthood", "pregnancy"]
                      },
                      { 
                        id: "newborn",
                        name: "New Born Baby Cover",
                        description: "Coverage for newborn baby from day 1",
                        keywords: ["newborn", "new born", "baby cover"]
                      },
                      { 
                        id: "critical_illness",
                        name: "Critical Illness Cover",
                        description: "Lump sum on diagnosis of critical illnesses",
                        keywords: ["critical illness", "ci cover", "critical care"]
                      },
                      { 
                        id: "opd",
                        name: "OPD / Outpatient Cover",
                        description: "Doctor visits, medicines without hospitalization",
                        keywords: ["opd", "outpatient", "befit", "out-patient"]
                      },
                      { 
                        id: "wellness",
                        name: "Wellness Benefits",
                        description: "Health checkups, fitness rewards",
                        keywords: ["wellness", "wellbeing", "optima wellbeing", "mental health wellbeing"]
                      },
                      { 
                        id: "hospital_cash",
                        name: "Hospital Daily Cash",
                        description: "Fixed daily amount during hospitalization",
                        keywords: ["hospital cash", "daily cash", "hospital daily"]
                      },
                      { 
                        id: "ped_reduction",
                        name: "PED Waiting Reduction",
                        description: "Reduces pre-existing disease waiting period",
                        keywords: ["ped", "pre-existing", "jumpstart", "ped waiver", "ped reduction", "instant cover"]
                      },
                      { 
                        id: "room_upgrade",
                        name: "Room Rent Waiver",
                        description: "Allows any room category without capping",
                        keywords: ["room", "room rent", "single private", "any room"]
                      },
                      { 
                        id: "restore",
                        name: "Unlimited Restore",
                        description: "Additional restore beyond base benefit",
                        keywords: ["unlimited restore", "unlimited care", "super restore", "recharge", "infinite care"]
                      },
                      { 
                        id: "air_ambulance",
                        name: "Air Ambulance Cover",
                        description: "Emergency air evacuation coverage",
                        keywords: ["air ambulance", "air evacuation"]
                      },
                      { 
                        id: "international",
                        name: "Worldwide Cover",
                        description: "Treatment coverage outside India",
                        keywords: ["international", "worldwide", "global", "overseas"]
                      },
                      { 
                        id: "chronic",
                        name: "Chronic Disease Mgmt",
                        description: "OPD cover for chronic conditions",
                        keywords: ["chronic", "abcd", "disease management"]
                      },
                      { 
                        id: "women_care",
                        name: "Women Care",
                        description: "Coverage for women-specific conditions",
                        keywords: ["women care", "women's health", "female"]
                      },
                      { 
                        id: "ncb_booster",
                        name: "NCB / Bonus Booster",
                        description: "Enhanced no-claim bonus",
                        keywords: ["cumulative bonus", "power booster", "loyalty bonus", "ncb booster", "super loyalty"]
                      },
                      { 
                        id: "sum_insured_booster",
                        name: "Sum Insured Booster",
                        description: "Increase sum insured beyond base limit",
                        keywords: ["limitless", "sum insured booster", "si booster", "inflation protector"]
                      },
                      { 
                        id: "senior_care",
                        name: "Senior Care Services",
                        description: "Value added services for seniors",
                        keywords: ["senior care", "senior citizen", "elderly"]
                      }
                    ];

                    // Function to find matching category for a rider
                    const findCategory = (riderName, riderDesc) => {
                      const searchText = `${riderName} ${riderDesc || ''}`.toLowerCase();
                      for (const cat of RIDER_CATEGORIES) {
                        for (const keyword of cat.keywords) {
                          if (searchText.includes(keyword.toLowerCase())) {
                            return cat.id;
                          }
                        }
                      }
                      return null;
                    };

                    // Group riders by category
                    const ridersByCategory = {};
                    const otherRiders = [];

                    plans.forEach(plan => {
                      plan.riders?.forEach(rider => {
                        const categoryId = findCategory(rider.name, rider.description);
                        if (categoryId) {
                          if (!ridersByCategory[categoryId]) {
                            ridersByCategory[categoryId] = {};
                          }
                          ridersByCategory[categoryId][plan.id] = rider;
                        } else {
                          otherRiders.push({ planId: plan.id, rider });
                        }
                      });
                    });

                    // Mobile: Card-based view for active plan only
                    if (isMobile) {
                      const activePlan = plans[activePlanIndex];
                      return (
                        <div className="p-4 space-y-3">
                          {RIDER_CATEGORIES.map((category, idx) => {
                            const categoryRiders = ridersByCategory[category.id];
                            if (!categoryRiders || Object.keys(categoryRiders).length === 0) return null;
                            
                            const planRider = categoryRiders[activePlan?.id];
                            
                            return (
                              <div
                                key={category.id}
                                className={`p-3 rounded-lg ${idx % 2 === 0 ? 'bg-blue-900/20' : 'bg-blue-900/10'}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className="text-[#1B2A4A] font-medium text-sm">{category.name}</p>
                                    <p className="text-[#3A4557] text-xs">{category.description}</p>
                                  </div>
                                  {planRider ? (
                                    <div className="flex-shrink-0">
                                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-600 text-xs">
                                        <Check className="h-3 w-3" />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex-shrink-0">
                                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-500/20 text-[#3A4557] text-xs">
                                        <X className="h-3 w-3" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {planRider && (
                                  <div className="mt-2 pl-0">
                                    <p className="text-[#A87608] text-xs font-medium">{planRider.name}</p>
                                    {planRider.waiting_period && (
                                      <p className="text-xs text-[#3A4557] mt-1">WP: {planRider.waiting_period}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Desktop: Grid view
                    return (
                      <div className="p-6"><Swipeable hint>
                        <div style={{ minWidth: plans.length >= 5 ? `${200 + plans.length * 160}px` : '600px' }}>
                          {RIDER_CATEGORIES.map((category, idx) => {
                            const categoryRiders = ridersByCategory[category.id];
                            if (!categoryRiders || Object.keys(categoryRiders).length === 0) return null;

                            return (
                              <div
                                key={category.id}
                                className={`grid gap-4 p-4 ${idx % 2 === 0 ? 'bg-blue-900/20' : ''} rounded-lg mb-2`}
                                style={{ 
                                  gridTemplateColumns: plans.length >= 5
                                    ? `200px repeat(${plans.length}, minmax(160px, 1fr))`
                                    : `250px repeat(${plans.length}, minmax(200px, 1fr))`
                                }}
                              >
                                <div>
                                  <p className="text-[#1B2A4A] font-semibold text-sm">{category.name}</p>
                                  <p className="text-[#3A4557] text-xs mt-0.5">{category.description}</p>
                                </div>
                                
                                {plans.map(plan => {
                                  const planRider = categoryRiders[plan.id];
                                  if (planRider) {
                                    return (
                                      <div key={plan.id} className="text-center">
                                        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-600 text-xs mb-2">
                                          <Check className="h-3 w-3" />
                                          Available
                                        </div>
                                        <p className="text-[#1B2A4A] text-xs font-medium">{planRider.name}</p>
                                        {planRider.waiting_period && (
                                          <p className="text-xs text-[#A87608] flex items-center justify-center gap-1 mt-1">
                                            <Clock className="h-3 w-3" />
                                            WP: {planRider.waiting_period}
                                          </p>
                                        )}
                                        {planRider.details && planRider.details[0] && (
                                          <p className="text-[#3A4557] text-xs mt-1 line-clamp-2">{planRider.details[0]}</p>
                                        )}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={plan.id} className="text-center flex items-center justify-center">
                                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-500/20 text-[#3A4557] text-xs">
                                        <X className="h-3 w-3" />
                                        Not Available
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </Swipeable></div>
                    );
                  })()}
                </div>
              )}
            </motion.div>

            {/* Disclaimer */}
            <div className="glass-card p-6 mt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-[#A87608] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-[#1B2A4A] mb-2">Important Disclaimer</h4>
                  <p className="text-[#3A4557] text-sm">
                    This comparison is for informational purposes only. Please refer to the official policy documents 
                    for complete terms and conditions. Coverage details, waiting periods, and benefits may vary based 
                    on the specific plan variant and sum insured chosen. Always consult with your insurance advisor 
                    before making a decision.
                  </p>
                </div>
              </div>
            </div>
            </div> {/* End of comparisonRef */}
          </>
        )}

        {selectedPlans.length === 1 && (
          <div className="glass-card p-12 text-center">
            <Scale className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
            <p className="text-[#3A4557]">Please select at least 2 plans to compare</p>
          </div>
        )}
      </main>
    </div>
  );
}
